import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { MDLINK_LOGO } from "@/lib/brand";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, LogOut, LayoutGrid, ArrowLeft } from "lucide-react";

export default function AppHeader({ subtitle, backTo }) {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const initials = (user?.name || user?.email || "?")
        .split(/[\s@.]/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join("");

    return (
        <header
            data-testid="app-header"
            className="no-print sticky top-0 z-40 border-b border-slate-200 bg-white/85 backdrop-blur-xl"
        >
            <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center gap-4">
                {backTo && location.pathname !== "/" && (
                    <button
                        data-testid="header-back-btn"
                        onClick={() => navigate(backTo)}
                        className="h-8 w-8 grid place-items-center rounded-md border border-slate-200 hover:bg-slate-50 transition-all duration-150"
                        aria-label="Volver"
                    >
                        <ArrowLeft className="h-4 w-4 text-slate-600" />
                    </button>
                )}
                <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
                    <img
                        src={MDLINK_LOGO}
                        alt="MD Link"
                        className="h-8 w-auto"
                        draggable={false}
                    />
                    <div className="hidden sm:block leading-tight pl-3 border-l border-slate-200">
                        <div className="font-heading font-semibold text-[13px] tracking-tight text-slate-900">
                            Cotizador
                        </div>
                        <div className="text-[10.5px] font-mono text-slate-500 uppercase tracking-widest">
                            {subtitle || "Plataforma B2B"}
                        </div>
                    </div>
                </Link>
                <div className="flex-1" />

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            data-testid="user-menu-trigger"
                            className="flex items-center gap-2 pl-1 pr-2 h-9 rounded-md border border-slate-200 hover:bg-slate-50 transition-all duration-150"
                        >
                            <div className="h-7 w-7 grid place-items-center rounded-sm bg-slate-900 text-white text-xs font-semibold font-mono">
                                {initials || "MD"}
                            </div>
                            <div className="text-sm text-slate-700 hidden sm:block">
                                {user?.name || user?.email}
                            </div>
                            <ChevronDown className="h-4 w-4 text-slate-500" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel className="text-xs font-mono uppercase tracking-widest text-slate-500">
                            {user?.email}
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            data-testid="menu-goto-dashboard"
                            onClick={() => navigate("/")}
                        >
                            <LayoutGrid className="h-4 w-4 mr-2" /> Dashboard
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            data-testid="menu-logout"
                            onClick={logout}
                            className="text-red-600 focus:text-red-600"
                        >
                            <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </header>
    );
}
